import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@api";
import "./OrganizationHome.css";
import { useAuth } from "../../../../context/AuthContext.jsx";

function formatTemplateName(page = {}) {
  if (page.page_type === "system") {
    if (page.system_key === "players") return "Players";
    if (page.system_key === "schedule") return "Schedule";
    if (page.system_key === "standings") return "Standings";
    if (page.system_key === "leaderboard") return "Leaderboard";
    if (page.system_key === "tournament_bracket") {
      return "Tournament Bracket";
    }
    return "System";
  }

  const templateId = page.template_id || "";

  if (!templateId) return "Basic";
  if (templateId === "default-homepage") return "Basic";
  if (templateId === "fab-homepage") return "FAB";
  return templateId;
}

function formatPagePath(path = "", isHomepage = false) {
  if (isHomepage || !path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}
function slugifyPagePath(value = "") {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const SYSTEM_PAGE_OPTIONS = [
  { key: "players", name: "Players", path: "players" },
  { key: "standings", name: "Standings", path: "standings" },
  { key: "schedule", name: "Schedule", path: "schedule" },
  { key: "leaderboard", name: "Leaderboard", path: "leaderboard" },
  {
    key: "tournament_bracket",
    name: "Tournament Bracket",
    path: "tournament-bracket",
  },
];









function OrganizationHome() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkAuth, user } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [pages, setPages] = useState([]);
  const [isAddPageModalOpen, setIsAddPageModalOpen] = useState(false);
  const [pageForm, setPageForm] = useState({
    name: "",
    pageKind: "page",
    systemKey: "",
  });
  const [activePageTab, setActivePageTab] = useState("league");
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renamePageId, setRenamePageId] = useState(null);
  const [renamePageName, setRenamePageName] = useState("");
  const [myClaims, setMyClaims] = useState([]);
  const isSuperAdmin = user?.role === "super_admin";
  const existingSystemPageKeys = pages
    .filter((page) => page.page_type === "system" && page.system_key)
    .map((page) => page.system_key);

  const availableSystemPageOptions = SYSTEM_PAGE_OPTIONS.filter(
    (item) => !existingSystemPageKeys.includes(item.key)
  );

  const handleCreatePage = async (e) => {
    e.preventDefault();

    try {
      if (isSuperAdmin && pageForm.pageKind === "system") {
        if (!pageForm.systemKey) {
          alert("Please select a system page.");
          return;
        }
        if (!availableSystemPageOptions.length) {
          alert("All system pages have already been added.");
          return;
        }
        const selectedSystemPage = availableSystemPageOptions.find(
          (item) => item.key === pageForm.systemKey
        );

        if (!selectedSystemPage) {
          alert("Invalid system page.");
          return;
        }

        await api.post(`/api/organization-page/${org.id}`, {
          name: selectedSystemPage.name,
          page_type: "system",
          system_key: selectedSystemPage.key,
          path: selectedSystemPage.path,
          is_homepage: false,
          is_published: false,
          show_in_navbar: false,
        });
      } else {
        const trimmedName = pageForm.name.trim();

        if (!trimmedName) {
          alert("Page name is required.");
          return;
        }

        await api.post(`/api/organization-page/${org.id}`, {
          name: trimmedName,
          type: "page",
          page_type: "custom",
          path: slugifyPagePath(trimmedName),
          is_homepage: false,
          is_published: false,
          show_in_navbar: false,
          
        });
      }

      setPageForm({
        name: "",
        pageKind: "page",
        systemKey: "",
      });

      setIsAddPageModalOpen(false);
      await loadPages();
    } catch (e) {
      console.error("Add page failed", e);
      alert(e?.response?.data?.message || "Could not add page.");
    }
  };




  const loadPages = async () => {
    try {
      const pagesRes = await api.get(`/api/organization-page/${id}`);
      setPages(pagesRes.data?.pages || []);
    } catch (e) {
      console.error("Pages fetch failed", e);
    }
  };



  const handleOpenRenameModal = (page) => {
    setRenamePageId(page.id);
    setRenamePageName(page.name || "");
    setIsRenameModalOpen(true);
  };

  const handleRenamePage = async (e) => {
    e.preventDefault();
    const trimmedName = renamePageName.trim();
    if (!trimmedName || !renamePageId) {
      alert("Page name is required.");
      return;
    }
    try {
      await api.put(`/api/organization-page/${org.id}/${renamePageId}`, {
        name: trimmedName,
      });
      setIsRenameModalOpen(false);
      setRenamePageId(null);
      setRenamePageName("");
      await loadPages();
    } catch (e) {
      console.error("Rename page failed", e);
      alert("Could not rename page.");
    }
  };



  useEffect(() => {
    const msg = sessionStorage.getItem("dashboard_success_message");
    if (msg) {
      setSuccessMessage(msg);
      sessionStorage.removeItem("dashboard_success_message");

      const t = setTimeout(() => {
        setSuccessMessage("");
      }, 3000);

      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const orgRes = await api.get(`/api/organizations/${id}`);

        if (!alive) return;
        setOrg(orgRes.data?.organization || null);
        const pagesRes = await api.get(`/api/organization-page/${id}`);

        if (!alive) return;
        setPages(pagesRes.data?.pages || []);
        const claimsRes = await api.get("/api/directory/my-leagues");

        if (!alive) return;
        setMyClaims(claimsRes.data || []);
      } catch (e) {
        console.error("Org fetch failed", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  const handleMovePage = async (index, direction) => {
    const newPages = [...displayPages];
    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newPages.length) return;

    const current = newPages[index];
    const target = newPages[targetIndex];

    newPages[index] = target;
    newPages[targetIndex] = current;

    const payload = newPages.map((page, i) => ({
      id: page.id,
      sort_order: i,
    }));

    try {
      await api.put(`/api/organization-page/${org.id}/reorder`, {
        pages: payload,
      });
      await loadPages();
    } catch (err) {
      console.error("Reorder failed", err);
      alert("Could not reorder pages.");
    }
  };
  const handleAddPage = () => {
    setPageForm({
      name: "",
      pageKind:
        isSuperAdmin && availableSystemPageOptions.length > 0 ? "system" : "page",
      systemKey: "",
    });
    setIsAddPageModalOpen(true);
  };

  const handleViewPublicPage = (path = "") => {
    const isLocal = window.location.hostname === "localhost";

    const formattedPath = path
      ? path.startsWith("/")
        ? path
        : `/${path}`
      : "";

    let url = "";

    if (org?.domain) {
      const cleanDomain = org.domain.replace(/^https?:\/\//, "").replace(/^www\./, "");
      url = isLocal
        ? `http://${cleanDomain}.localhost:5173${formattedPath}`
        : `https://${cleanDomain}${formattedPath}`;
    } else if (org?.slug) {
      url = isLocal
        ? `http://localhost:5173/${org.slug}${formattedPath}`
        : `https://sportinghip.com/${org.slug}${formattedPath}`;
    } else {
      return;
    }
    window.open(url, "_blank");
  };
  const handleStartTrial = async () => {
    try {
      const res = await api.post(
        `/api/organizations/${org.id}/start-trial`,
        {},
        { withCredentials: true }
      );

      setOrg(res.data?.organization || null);
      await loadPages();
      await checkAuth();
      setSuccessMessage("Free trial started successfully.");
      setTimeout(() => setSuccessMessage(""), 3000);
      navigate("/dashboard/seasons")
    } catch (err) {
      console.error("Start trial failed", err);
      alert(err.response?.data?.message || "Could not start free trial.");
    }
  };
  const handleToggleWebsiteVisibility = async () => {
    try {
      const formData = new FormData();
      formData.append("website_visible", String(!org.website_visible));

      const res = await api.put(`/api/onboarding/organizations/${org.id}`, formData);

      setOrg((prev) => ({
        ...prev,
        ...(res.data?.organization || res.data || {}),
      }));
    } catch (err) {
      console.error("Website visibility update failed", err);
      alert("Could not update website visibility.");
    }
  };

  if (loading) return null;
  if (!org) {

    return (
      <div className="org-home-page">
        <div className="org-home-card">
          <h2 className="org-home-title">Website</h2>
          <p className="org-home-subtext">Organization not found.</p>
          <button
            className="org-home-btn org-home-btn-ghost"
            onClick={() => navigate("/dashboard/dashboard-start")}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  const publicUrl = org.domain
    ? org.domain
    : org.slug
      ? `sportinghip.com/${org.slug}`
      : "—";
  const isFreeListingPlan = org?.plan === "free";
  const isTrialPlan = org?.plan === "trial";
  const isListingPlusPlan = org?.plan === "listing_plus";
  const isLeagueToolsPlan = org?.plan === "league_tools";
  const isBrandedSitePlan = org?.plan === "branded_site";
  const isCustomDomainPlan = org?.plan === "custom_domain";

  const canStartTrial = isFreeListingPlan && !org?.has_used_trial;

  const canUseManagementPages =
    isTrialPlan ||
    isLeagueToolsPlan ||
    isBrandedSitePlan ||
    isCustomDomainPlan;

  const canSeeWebsiteSettings =
    isSuperAdmin || isBrandedSitePlan || isCustomDomainPlan;

  const displayPages = canUseManagementPages
    ? pages
    : pages.filter((page) => page.page_type !== "system");


  const leaguePages = displayPages.filter(
    (page) => page.page_type === "system"
  );

  const websitePages = displayPages.filter(
    (page) => page.page_type !== "system"
  );

  const hasLeaguePages = leaguePages.length > 0;
  const hasWebsitePages = websitePages.length > 0;
  const shouldShowPageTabs = hasLeaguePages && hasWebsitePages;

  const visiblePageSection = shouldShowPageTabs
    ? activePageTab
    : hasLeaguePages
      ? "league"
      : "website";

  const realHomepage = pages.find(
    (page) => page.is_homepage || page.type === "home" || page.path === ""
  );
  const trialEndsAt = org?.trial_ends_at ? new Date(org.trial_ends_at) : null;
  const msLeft = trialEndsAt ? trialEndsAt.getTime() - Date.now() : 0;

  let trialTimeLeftLabel = "";

  if (isTrialPlan && trialEndsAt) {
    if (msLeft <= 0) {
      trialTimeLeftLabel = "Trial expired";
    } else if (msLeft < 60 * 60 * 1000) {
      const minutesLeft = Math.ceil(msLeft / (1000 * 60));
      trialTimeLeftLabel = `${minutesLeft} minute${minutesLeft === 1 ? "" : "s"} left in your trial`;
    } else if (msLeft < 24 * 60 * 60 * 1000) {
      const hoursLeft = Math.ceil(msLeft / (1000 * 60 * 60));
      trialTimeLeftLabel = `${hoursLeft} hour${hoursLeft === 1 ? "" : "s"} left in your trial`;
    } else {
      const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
      trialTimeLeftLabel = `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your trial`;
    }
  }
  const approvedClaim = myClaims.find((claim) => claim.status === "approved");
  const pendingClaim = myClaims.find((claim) => claim.status === "pending");
  const rejectedClaim = myClaims.find((claim) => claim.status === "rejected");

  console.log(pages)

  return (
    <div className="org-home-page">
      <div className="org-home-card">
        {successMessage ? (
          <div className="org-page-success-alert">{successMessage}</div>
        ) : null}

        {isSuperAdmin && (
          <div className="org-home-overview-card">
            <div className="org-home-header">
              <div>
                <h1 className="org-home-heading">Website</h1>

                <div className="org-home-meta-block">
                  <div className="org-home-meta-row">
                    <span className="org-home-meta-label">Organization</span>
                    <span className="org-home-meta-value">{org.name || "—"}</span>
                  </div>

                  <div className="org-home-meta-row">
                    <span className="org-home-meta-label">Public URL</span>
                    <a
                      className="org-home-url-link"
                      href={`https://${publicUrl}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {publicUrl}
                    </a>
                  </div>

                  <div className="org-home-meta-row">
                    <span className="org-home-meta-label">Email</span>
                    <span className="org-home-meta-value">
                      {org.contact_email || "—"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="org-home-header-right">
                <div className="org-home-visibility-pill-wrap">
                  <button
                    type="button"
                    className={`org-home-visibility-pill ${org?.website_visible ? "is-live" : "is-hidden"
                      }`}
                    onClick={handleToggleWebsiteVisibility}
                    title={
                      org?.website_visible
                        ? "Click to hide website"
                        : "Click to make website live"
                    }
                  >
                    
                    <span className="org-home-visibility-dot" />
                    <span className="org-home-visibility-text">
                      {org?.website_visible ? "Live" : "Hidden"}
                    </span>
                  </button>
                </div>

                <button
                  className="org-home-btn org-home-btn-ghost"
                  onClick={() =>
                    navigate(`/dashboard/organizations/${org.id}/website-settings`)
                  }
                >
                  Website Settings
                </button>
                <button
                  className="org-home-btn org-home-btn-ghost"
                  onClick={() =>
                    navigate(`/dashboard/organizations/${org.id}/navbar-settings`)
                  }
                >
                  Navbar Settings
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="org-home-pages-card">
          <div className="org-home-toolbar">
            <div>
              <h1 className="org-home-section-title">Public Pages</h1>
              <p className="org-home-section-subtext">
                Manage the public pages shown on your organization site.
              </p>
            </div>
            <div className="org-home-toolbar-actions">
              {isSuperAdmin && (
                <button
                  className="org-home-btn org-home-btn-primary"
                  onClick={handleAddPage}
                >
                  Add Page / System Page
                </button>
              )}
              {shouldShowPageTabs && (
                <div className="org-home-page-tabs">
                  <button
                    type="button"
                    className={`org-home-page-tab ${activePageTab === "league" ? "is-active" : ""}`}
                    onClick={() => setActivePageTab("league")}
                  >
                    League Pages
                  </button>

                  <button
                    type="button"
                    className={`org-home-page-tab ${activePageTab === "website" ? "is-active" : ""}`}
                    onClick={() => setActivePageTab("website")}
                  >
                    Website Pages
                  </button>
                </div>
              )}
            </div>

          </div>

          {!realHomepage && (
            <button
              className="org-home-btn org-home-btn-ghost"
              onClick={async () => {
                try {
                  await api.post(`/api/organization-page/${org.id}`, {
                    name: "Home",
                    type: "home",
                    path: "",
                    template_id: "default-homepage",
                    is_homepage: true,
                    is_published: true,
                  });

                  await loadPages();
                } catch (e) {
                  console.error("Create homepage failed", e);
                  alert("Could not create homepage.");
                }
              }}
            >
              Add Homepage
            </button>
          )}

          <div className="org-home-pages-wrap">
            <div className="org-home-pages-head">
              <div>Page</div>
              <div>Path</div>
              <div>Template</div>
              <div>Navbar</div>
              <div>Actions</div>
            </div>
          </div>

          {visiblePageSection === "league" && hasLeaguePages && (
            <>
              <div className="org-home-pages-list">
                {leaguePages.map((page) => {
                  const rowPath = formatPagePath(page.path, page.is_homepage);
                  const isHomepageRow =
                    page.is_homepage || page.type === "home" || page.path === "";

                  return (
                    <div className="org-home-page-row" key={page.id}>
                      <div className="org-home-page-main">
                        <div className="org-home-page-name-row">
                          <div className="org-home-page-name">{page.name}</div>
                        </div>
                      </div>

                      <div className="org-home-page-path">{rowPath}</div>

                      <div className="org-home-page-template">
                        {formatTemplateName(page)}
                      </div>

                      <div className="org-home-page-status">
                        {/* <button
                          type="button"
                          className={`org-home-status-toggle ${page.is_published ? "is-on" : "is-off"
                            } ${isHomepageRow ? "is-disabled" : ""}`}
                          disabled={isHomepageRow}
                          onClick={async () => {
                            if (isHomepageRow) return;

                            try {
                              await api.put(`/api/organization-page/${org.id}/${page.id}`, {
                                is_published: !page.is_published,
                              });

                              await loadPages();
                            } catch (err) {
                              console.error("Update published failed", err);
                              alert(
                                err?.response?.data?.message ||
                                "Could not update page status."
                              );
                            }
                          }}
                        >
                          <span className="org-home-status-toggle-track">
                            <span className="org-home-status-toggle-thumb" />
                          </span>

                          <span className="org-home-status-toggle-label">
                            {isHomepageRow
                              ? "Homepage"
                              : page.is_published
                                ? "Published"
                                : "Draft"}
                          </span>
                        </button> */}

                        {!isHomepageRow && (
                          <button
                            type="button"
                            className={`org-home-status-toggle ${page.show_in_navbar ? "is-on" : "is-off"
                              }`}
                            onClick={async () => {
                              try {
                                await api.put(`/api/organization-page/${org.id}/${page.id}`, {
                                  show_in_navbar: !page.show_in_navbar,
                                });

                                await loadPages();
                              } catch (err) {
                                console.error("Update navbar visibility failed", err);
                                alert(
                                  err?.response?.data?.message ||
                                  "Could not update navbar visibility."
                                );
                              }
                            }}
                          >
                            <span className="org-home-status-toggle-track">
                              <span className="org-home-status-toggle-thumb" />
                            </span>

                            <span className="org-home-status-toggle-label">
                              {page.show_in_navbar ? "Visible" : "Hidden"}
                            </span>
                          </button>
                        )}
                      </div>

                      <div className="org-home-page-actions">
                        <button
                          type="button"
                          className="org-home-row-btn org-home-row-btn-ghost"
                          onClick={() => handleViewPublicPage(page.is_homepage ? "" : page.path)}
                          disabled={!(org.slug || org.domain) || !page.is_published}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className={`org-home-row-btn ${page.is_published
                              ? "org-home-row-btn-danger"
                              : "org-home-row-btn-ghost"
                            }`}
                          onClick={async () => {
                            try {
                              const willPublish = !page.is_published;

                              await api.put(`/api/organization-page/${org.id}/${page.id}`, {
                                is_published: willPublish,
                                ...(willPublish ? {} : { show_in_navbar: false }),
                              });

                              await loadPages();
                            } catch (err) {
                              console.error("Update page availability failed", err);
                              alert(
                                err?.response?.data?.message ||
                                "Could not update page availability."
                              );
                            }
                          }}
                        >
                          {page.is_published ? "Disable" : "Enable"}
                        </button>
                        <button
                          type="button"
                          className="org-home-row-btn org-home-row-btn-ghost"
                          onClick={() =>
                            navigate(`/dashboard/${org.id}/system-pages/${page.id}/settings`)
                          }
                        >
                          Configure
                        </button>

                        <button
                          type="button"
                          className="org-home-row-btn org-home-row-btn-ghost"
                          onClick={() => handleOpenRenameModal(page)}
                        >
                          Rename
                        </button>

                        {isSuperAdmin &&
                          !(page.is_homepage || page.type === "home" || page.path === "") ? (
                          <button
                            type="button"
                            className="org-home-row-btn org-home-row-btn-danger"
                            onClick={async () => {
                              const confirmed = window.confirm(`Delete "${page.name}"?`);
                              if (!confirmed) return;

                              try {
                                await api.delete(`/api/organization-page/${org.id}/${page.id}`);
                                await loadPages();
                              } catch (e) {
                                console.error("Delete page failed", e);
                                alert(e?.response?.data?.message || "Could not delete page.");
                              }
                            }}
                          >
                            Delete
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {visiblePageSection === "website" && hasWebsitePages && (
            <>
              <div className="org-home-pages-list">
                {websitePages.map((page) => {
                  const rowPath = formatPagePath(page.path, page.is_homepage);
                  const isHomepageRow =
                    page.is_homepage || page.type === "home" || page.path === "";

                  return (
                    <div className="org-home-page-row" key={page.id}>
                      <div className="org-home-page-main">
                        <div className="org-home-page-name-row">
                          <div className="org-home-page-name">{page.name}</div>
                        </div>
                      </div>

                      <div className="org-home-page-path">{rowPath}</div>

                      <div className="org-home-page-template">
                        {formatTemplateName(page)}
                      </div>

                      <div className="org-home-page-status">
                        {/* <button
                          type="button"
                          className={`org-home-status-toggle ${page.is_published ? "is-on" : "is-off"
                            } ${isHomepageRow ? "is-disabled" : ""}`}
                          disabled={isHomepageRow}
                          onClick={async () => {
                            if (isHomepageRow) return;

                            try {
                              await api.put(`/api/organization-page/${org.id}/${page.id}`, {
                                is_published: !page.is_published,
                              });

                              await loadPages();
                            } catch (err) {
                              console.error("Update published failed", err);
                              alert(
                                err?.response?.data?.message ||
                                "Could not update page status."
                              );
                            }
                          }}
                        >
                          <span className="org-home-status-toggle-track">
                            <span className="org-home-status-toggle-thumb" />
                          </span>

                          <span className="org-home-status-toggle-label">
                            {isHomepageRow
                              ? "Homepage"
                              : page.is_published
                                ? "Published"
                                : "Draft"}
                          </span>
                        </button> */}


                        <button
                          type="button"
                          className={`org-home-status-toggle ${page.show_in_navbar ? "is-on" : "is-off"
                            }`}
                          onClick={async () => {
                            try {
                              await api.put(`/api/organization-page/${org.id}/${page.id}`, {
                                show_in_navbar: !page.show_in_navbar,
                              });

                              await loadPages();
                            } catch (err) {
                              console.error("Update navbar visibility failed", err);
                              alert(
                                err?.response?.data?.message ||
                                "Could not update navbar visibility."
                              );
                            }
                          }}
                        >
                          <span className="org-home-status-toggle-track">
                            <span className="org-home-status-toggle-thumb" />
                          </span>

                          <span className="org-home-status-toggle-label">
                            {page.show_in_navbar ? "Visible" : "Hidden"}
                          </span>
                        </button>

                      </div>

                      <div className="org-home-page-actions">
                        <button
                          type="button"
                          className="org-home-row-btn org-home-row-btn-ghost"
                          onClick={() => handleViewPublicPage(page.is_homepage ? "" : page.path)}
                          disabled={!(org.slug || org.domain) || !page.is_published}
                        >
                          View
                        </button>
                        {!isHomepageRow && (
                          <button
                            type="button"
                            className={`org-home-row-btn ${page.is_published
                                ? "org-home-row-btn-danger"
                                : "org-home-row-btn-ghost"
                              }`}
                            onClick={async () => {
                              const willPublish = !page.is_published;

                              try {
                                await api.put(`/api/organization-page/${org.id}/${page.id}`, {
                                  is_published: willPublish,
                                  ...(willPublish ? {} : { show_in_navbar: false }),
                                });

                                await loadPages();
                              } catch (err) {
                                console.error("Update page availability failed", err);
                                alert(
                                  err?.response?.data?.message ||
                                  "Could not update page availability."
                                );
                              }
                            }}
                          >
                            {page.is_published ? "Disable" : "Enable"}
                          </button>
                        )}
                        <button
                          type="button"
                          className="org-home-row-btn org-home-row-btn-ghost"
                          onClick={() =>
                            navigate(`/dashboard/page-builder/${org.id}/pages/${page.id}/edit`)
                          }
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className="org-home-row-btn org-home-row-btn-ghost"
                          onClick={() => handleOpenRenameModal(page)}
                        >
                          Rename
                        </button>

                        {isSuperAdmin &&
                          !(page.is_homepage || page.type === "home" || page.path === "") ? (
                          <button
                            type="button"
                            className="org-home-row-btn org-home-row-btn-danger"
                            onClick={async () => {
                              const confirmed = window.confirm(`Delete "${page.name}"?`);
                              if (!confirmed) return;

                              try {
                                await api.delete(`/api/organization-page/${org.id}/${page.id}`);
                                await loadPages();
                              } catch (e) {
                                console.error("Delete page failed", e);
                                alert(e?.response?.data?.message || "Could not delete page.");
                              }
                            }}
                          >
                            Delete
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {
            ((isTrialPlan && trialEndsAt) || canStartTrial || (isFreeListingPlan && org?.has_used_trial)) && (
              <div className="org-home-trial-section">
                {isTrialPlan && trialEndsAt && (
                  <div className="org-home-trial-card">
                    <div>
                      <h3 className="org-home-section-title">{trialTimeLeftLabel}</h3>
                      <p className="org-home-section-subtext">
                        Your free trial ends on {trialEndsAt.toLocaleString()}.
                      </p>
                    </div>
                  </div>
                )}

                {canStartTrial && (
                  <div className="org-home-trial-card">
                    <div>
                      <h3 className="org-home-section-title">Try League Management</h3>
                      <p className="org-home-section-subtext">
                        Test teams, players, schedule, standings, and other management pages with a limited free trial.
                      </p>
                    </div>

                    <button
                      className="org-home-btn org-home-btn-primary"
                      onClick={handleStartTrial}
                    >
                      Start Free Trial
                    </button>
                  </div>
                )}

                {isFreeListingPlan && org?.has_used_trial && (
                  <div className="org-home-trial-card">
                    <div>
                      <h3 className="org-home-section-title">League Management Trial Used</h3>
                      <p className="org-home-section-subtext">
                        Your free trial has already been used. Upgrade to continue using management pages.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )
          }
        </div>
      </div>

      {isRenameModalOpen && (
        <div className="orgs-modal" onClick={() => setIsRenameModalOpen(false)}>
          <div className="orgs-modal__content" onClick={(e) => e.stopPropagation()}>
            <h3>Rename Page</h3>

            <form onSubmit={handleRenamePage} className="orgs-modal__form">
              <input
                type="text"
                placeholder="Page name"
                value={renamePageName}
                onChange={(e) => setRenamePageName(e.target.value)}
                required
              />

              <div className="orgs-modal__actions">
                <button type="submit">Save</button>
                <button
                  type="button"
                  onClick={() => {
                    setIsRenameModalOpen(false);
                    setRenamePageId(null);
                    setRenamePageName("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}




      {isAddPageModalOpen && (
        <div className="orgs-modal" onClick={() => setIsAddPageModalOpen(false)}>
          <div className="orgs-modal__content" onClick={(e) => e.stopPropagation()}>
            <h3>Add Page</h3>

            <form onSubmit={handleCreatePage} className="orgs-modal__form">
              {isSuperAdmin && (
                <select
                  value={pageForm.pageKind}
                  onChange={(e) =>
                    setPageForm((prev) => ({
                      ...prev,
                      pageKind: e.target.value,
                      systemKey: "",
                      name: "",
                    }))
                  }
                >
                  <option value="page">Regular Page</option>
                  {availableSystemPageOptions.length > 0 && (
                    <option value="system">System Page</option>
                  )}
                </select>
              )}

              {isSuperAdmin && pageForm.pageKind === "system" ? (
                <>
                  <select
                    value={pageForm.systemKey}
                    onChange={(e) =>
                      setPageForm((prev) => ({
                        ...prev,
                        systemKey: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select system page</option>
                    {availableSystemPageOptions.map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.name}
                      </option>
                    ))}
                  </select>

                  <div className="org-home-subtext" style={{ marginTop: 8 }}>
                    URL: {org?.domain
                      ? `${org.domain}/${pageForm.systemKey || "system-page"}`
                      : org?.slug
                        ? `sportinghip.com/${org.slug}/${pageForm.systemKey || "system-page"}`
                        : `/${pageForm.systemKey || "system-page"}`}
                  </div>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Page name"
                    value={pageForm.name}
                    onChange={(e) =>
                      setPageForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    required
                  />

                  <div className="org-home-subtext" style={{ marginTop: 8 }}>
                    URL: {org?.domain
                      ? `${org.domain}/${slugifyPagePath(pageForm.name) || "page-name"}`
                      : org?.slug
                        ? `sportinghip.com/${org.slug}/${slugifyPagePath(pageForm.name) || "page-name"}`
                        : `/${slugifyPagePath(pageForm.name) || "page-name"}`}
                  </div>
                </>
              )}

              <div className="orgs-modal__actions">
                <button type="submit">Create</button>
                <button type="button" onClick={() => setIsAddPageModalOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
    </div>
  );
}

export default OrganizationHome; 
